import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import Card from '../../../components/ui/Card';
import { LoadingSpinner } from '../../../components/ui/LoadingSpinner';
import EmployeeInformation from '../../../components/ui/EmployeeInformation';
import Dialog from '../../../components/ui/Dialog';
import NotYouDialog from '../../../components/ui/NotYouDialog';
import { useAuth } from '../../../hooks/useAuth';
import { teamReviewService } from '../../../services/teamReviewService';
import { hodReviewService } from '../../../services/hodReviewService';
import { getNominationErrorMessage } from '../../../utils/nominationErrorUtils';
import arrowBackIcon from '../../../assets/arrow_back.svg';
// import tabReviewIcon from '../../../assets/tab_review.svg';
import tabViewFeedbackIcon from '../../../assets/tab_view_feedback.svg';
import tabViewOKRsIcon from '../../../assets/tab_view_OKRs.svg';
// Update the import path if the file exists elsewhere, for example:
//import TeamRatingsFeedbacks from "../../../components//ui/TeamRatingsFeedbacksUi";
// Or create the file at ../team_review/TeamRatingsFeedbacks.tsx if it does not exist.

import NoDataState from '../../../components/ui/NoDataState';
import NoActiveCycle from '../../../components/ui/NoActiveCycle';
import { useActiveCycle } from '../../../hooks/useActiveCycle';
import { useRBAC } from '../../../hooks/useRBAC';
import { getOkrResultColumnLabel } from '../../../utils/cycleUtils';


// Team Review Detail interface
interface ReviewData {
  [key: string]: unknown;
}

interface TeamReviewDetail {
  id: string;
  employeeId: string;
  employeeName: string;
  designation: string;
  department: string;
  managerName: string;
  reviewStatus: string; // Using dynamic status from API instead of hardcoded values
  employeeEmail: string;
  reviewData?: ReviewData; // Will contain the actual review data
  submittedDate?: string;
  lastModified?: string;
}

interface NominationReviewsData {
  status?: string;
  reviewee?: {
    employee_id?: string;
    name?: string;
    designation?: string;
    department?: string;
    email?: string;
    manager?: {
      manager_name?: string;
      manager_email?: string;
    };
  };
  [key: string]: unknown;
}

const HODReviewDetailPageView: React.FC = () => {
  const { nominationRelationshipId } = useParams<{ 
    nominationRelationshipId: string; 
  }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isLoading: authLoading } = useAuth();
  const { appraisalCycle } = useRBAC();
  const hasActiveCycle = useActiveCycle();
  const resultColumnLabel = getOkrResultColumnLabel(appraisalCycle?.cycle_type);
  
  // Get nomination relationship ID from route params or navigation state
  const getRelationshipId = useCallback(() => {
    if (nominationRelationshipId) {
      return nominationRelationshipId;
    }
    // Check navigation state as fallback
    const state = location.state as { nominationRelationshipId?: string } | null;
    return state?.nominationRelationshipId;
  }, [nominationRelationshipId, location.state]);
  
  const [reviewDetail, setReviewDetail] = useState<TeamReviewDetail | null>(null);
  const [loading, setLoading] = useState(true);
  type TabType = 'review' | 'feedback' | 'okrs' | 'hodReview';
  const [activeTab, setActiveTab] = useState<TabType>('okrs');
  const [nominationReviews, setNominationReviews] = useState<NominationReviewsData | null>(null);
  const [nominationReviewsLoading, setNominationReviewsLoading] = useState(false);
  const [missingRelationshipId, setMissingRelationshipId] = useState(false);
  
  // Form state
  const [ratings, setRatings] = useState<Record<string, string>>({});
  const [appreciativeFeedback, setAppreciativeFeedback] = useState('');
  const [constructiveFeedback, setConstructiveFeedback] = useState('');
  const [sendEmailCopy, setSendEmailCopy] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  // OKR rating state
  const [okrOverallRating, setOkrOverallRating] = useState<number | ''>('');
  
  // OKR data state
  const [okrData, setOkrData] = useState<any>(null);
  const [okrLoading, setOkrLoading] = useState(false);
  const [okrError, setOkrError] = useState<string | null>(null);

  // Feedback state
  const [feedback, setFeedback] = useState<{
    appreciativeFeedback: string[];
    constructiveFeedback: string[];
  } | null>(null);
  const [feedbackLoading, setFeedbackLoading] = useState(false);
  
  // Form validation states (matching nomination review page)
  
  // Check if form should be disabled (when status is submitted)
  const isFormDisabled = nominationReviews?.status?.toLowerCase() === 'submitted';

  
  // Validation function to check if form is complete (exact same as nomination review)
  const isFormComplete = () => {
    // Check if all radio buttons are selected
    const allRatingsSelected = Object.values(ratings).every(rating => rating !== '');
    
    // Check if both feedback fields have valid character count
    const appreciativeFeedbackValid = teamReviewService.isValidCharacterCount(appreciativeFeedback).isValid;
    const constructiveFeedbackValid = teamReviewService.isValidCharacterCount(constructiveFeedback).isValid;
    
    return allRatingsSelected && appreciativeFeedbackValid && constructiveFeedbackValid;
  };

  // Validation function to check if draft can be saved (exact same as nomination review)
  const isDraftSaveable = () => {
    // Check if any radio button is selected (always valid for draft)
    const anyRatingSelected = Object.values(ratings).some(rating => rating !== '');
    
    // Check if any feedback text is entered AND meets minimum character requirement
    const appreciativeFeedbackEntered = appreciativeFeedback.trim().length > 0;
    const constructiveFeedbackEntered = constructiveFeedback.trim().length > 0;
    
    // If text is entered, it must meet validation (10+ characters)
    const appreciativeFeedbackValid = appreciativeFeedbackEntered 
      ? teamReviewService.isValidCharacterCount(appreciativeFeedback).isValid 
      : true; // Empty is fine for draft
      
    const constructiveFeedbackValid = constructiveFeedbackEntered 
      ? teamReviewService.isValidCharacterCount(constructiveFeedback).isValid 
      : true; // Empty is fine for draft
    
    // Draft is saveable if:
    // 1. Any radio is selected, OR
    // 2. Any text is entered AND that text meets validation requirements
    const hasValidData = anyRatingSelected || 
      (appreciativeFeedbackEntered && appreciativeFeedbackValid) || 
      (constructiveFeedbackEntered && constructiveFeedbackValid);
    
    return hasValidData;
  };
  
  // Dialog states
  const [showNotYouDialog, setShowNotYouDialog] = useState(false);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [showSubmitDialog, setShowSubmitDialog] = useState(false);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const [showOKRSubmitDialog, setShowOKRSubmitDialog] = useState(false);
  const [okrSubmitting, setOkrSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // "Not You?" dialog state (matching nomination review page)
  const [notYouDialogState, setNotYouDialogState] = useState({
    isOpen: false,
    email: '',
    emailError: '',
    isSubmitting: false
  });

  // Fetch review details
  useEffect(() => {
    const fetchReviewDetail = async () => {
      if (authLoading || !user || !nominationRelationshipId) {
        return;
      }

      setLoading(true);
      
      try {
        console.log('🔄 Fetching team review details for nomination relationship ID:', nominationRelationshipId);
        
        // Get data from navigation state if available
        const state = location.state as { 
          employeeId?: string; 
          employeeName?: string; 
          designation?: string; 
          department?: string; 
          managerName?: string;
          employeeEmail?: string;
        } | null;
        
        // Create a minimal detail object with the nomination relationship ID and navigation state data
        const basicDetail: TeamReviewDetail = {
          id: nominationRelationshipId,
          employeeId: state?.employeeId ? `#${state.employeeId}` : '#88888888',
          employeeName: state?.employeeName || '',
          designation: state?.designation || '',
          department: state?.department || '',
          managerName: state?.managerName || '',
          reviewStatus: 'pending', // Default to pending until we get actual status from API
          employeeEmail: state?.employeeEmail || 'loading@company.com',
          submittedDate: undefined,
          lastModified: undefined
        };
        console.log('✅ Basic team review detail initialized:', basicDetail);
        console.log('Employee ID XXXXXXXXXXXXXX:', basicDetail.employeeId);
        setReviewDetail(basicDetail);
      } catch (error) {
        console.error('❌ Error fetching team review details:', error);
        setReviewDetail(null);
      } finally {
        setLoading(false);
      }
    };

    fetchReviewDetail();
  }, [nominationRelationshipId, user, authLoading, location.state]);

  const handleBackClick = () => {
    navigate('/hod-review');
  };

  // Function to fetch nomination review details when Review tab is clicked
  const fetchNominationReviews = useCallback(async () => {
    if (!user?.email) {
      console.error('Missing user email for nomination review details fetch');
      return;
    }

    // Get nomination_relationship_id dynamically from URL params or navigation state
    const relationshipId = getRelationshipId();
    if (!relationshipId) {
      console.error('Missing nomination_relationship_id for nomination review details fetch');
      setNominationReviews(null);
      return;
    }

    setNominationReviewsLoading(true);
    
    try {
      console.log('🔄 Fetching nomination review details for email:', user.email, 'and nomination_relationship_id:', relationshipId);
      const response = await hodReviewService.getNominationReviewDetails(user.email, relationshipId);
      
      if (response.status && response.data) {
  console.log('✅ Nomination review details fetched successfully:', response.data);
  
  console.log('Employee ID (API):', response.data?.reviewee?.employee_id);
        setNominationReviews(response.data);
        
        // Update reviewDetail with actual status from API
        if (response.data.status) {
          setReviewDetail(prev => prev ? {
            ...prev,
            reviewStatus: response.data.status
          } : prev);
        }
        
        // Populate form fields with existing data if available
        if (response.data.competency) {
          const competencyData = response.data.competency;
          setRatings({
            'rating-1': competencyData.solution_focused?.toString() || '',
            'rating-2': competencyData.adapt_to_win?.toString() || '',
            'rating-3': competencyData.courage_to_act?.toString() || '',
            'rating-4': competencyData.act_for_enterprise?.toString() || '',
            'rating-5': competencyData.own_it?.toString() || ''
          });
        }
        
        if (response.data.feedback) {
          setAppreciativeFeedback(response.data.feedback.appreciative || '');
          setConstructiveFeedback(response.data.feedback.constructive || '');
        }
      } else {
        console.warn('⚠️ Nomination review details fetch failed:', response.error);
        setNominationReviews(null);
      }
    } catch (error) {
      console.error('❌ Error fetching nomination review details:', error);
      setNominationReviews(null);
    } finally {
      setNominationReviewsLoading(false);
    }
  }, [user, getRelationshipId]);

  // Function to fetch HOD self appraisal OKR data
  const fetchHODOKRData = useCallback(async () => {
    const state = location.state as { employeeId?: string } | null;
    const employeeId = state?.employeeId || nominationReviews?.reviewee?.employee_id;
    
    if (!user?.email || !employeeId) {
      console.error('Missing user email or employee ID for OKR data fetch');
      return;
    }

    setOkrLoading(true);
    setOkrError(null);
    
    try {
      console.log('🔄 Fetching HOD OKR data using service method');
      
      // Use the service method instead of calling API directly
      const response = await hodReviewService.getHodViewOKRs(
        user.email, 
        employeeId
      );

      console.log('✅ HOD OKR service response:', response);
      
      if (response.status && response.data) {
        setOkrData(response.data);
        
        // Update the dropdown with hod_rating from response (top-level in data)
        if (response.data.hod_rating !== undefined && response.data.hod_rating !== null) {
          setOkrOverallRating(response.data.hod_rating);
        }
      } else {
        // Use error from service response
        const errorMessage = response.error || 'Failed to fetch OKR data';
        console.error('❌ Service returned unsuccessful response:', response);
        setOkrError(errorMessage);
      }
    } catch (error: any) {
      console.error('❌ Error fetching HOD OKR data:', error);
      console.error('Error details:', {
        message: error?.message,
        response: error?.response?.data,
        status: error?.response?.status,
        statusText: error?.response?.statusText
      });
      
      // Set user-friendly error message based on error type
      let errorMessage = 'Failed to fetch OKR data';
      
      if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error?.response?.status === 404) {
        errorMessage = 'OKR data not found for this employee';
      } else if (error?.response?.status === 403) {
        errorMessage = 'Not authorized to view OKR data';
      } else if (error?.response?.status >= 500) {
        errorMessage = 'Server error occurred while fetching OKR data';
      } else if (error?.message) {
        errorMessage = error.message;
      }
      
      setOkrError(errorMessage);
    } finally {
      setOkrLoading(false);
    }
  }, [user, nominationReviews, location.state]);

  // Fetch nomination review feedbacks for the Team Review and Feedback tab
  const fetchFeedback = useCallback(async () => {
    if (!user?.email) {
      console.error('Missing user email for feedback fetch');
      return;
    }

    const relationshipId = getRelationshipId();
    if (!relationshipId) {
      console.error('Missing nomination_relationship_id for feedback fetch');
      return;
    }

    setFeedbackLoading(true);

    try {
      console.log('🔄 Fetching nomination review feedbacks for email:', user.email, 'and nomination_relationship_id:', relationshipId);
      const response = await teamReviewService.getNominationReviewFeedbacks(user.email, relationshipId);

      if (response.status && response.data) {
        console.log('✅ Nomination review feedbacks fetched successfully:', response.data);

        // Populate nominationReviews from this response so EmployeeInformation is populated
        if (!nominationReviews && response.data.reviewee) {
          setNominationReviews(response.data);
        }

        const hasCompetency = response.data.competency && Object.keys(response.data.competency).length > 0;
        const hasFeedback = response.data.feedback && (
          (response.data.feedback.appreciative && String(response.data.feedback.appreciative).length > 0) ||
          (response.data.feedback.constructive && String(response.data.feedback.constructive).length > 0)
        );

        if (!hasCompetency && !hasFeedback) {
          setFeedback(null);
          return;
        }

        const flattenFeedback = (feedbackArray: any): string[] => {
          if (!feedbackArray) return [];
          if (Array.isArray(feedbackArray) && feedbackArray.length > 0 && Array.isArray(feedbackArray[0])) {
            return feedbackArray.map((item: any) =>
              Array.isArray(item) ? item.join(' ') : String(item)
            ).filter((item: string) => item.trim().length > 0);
          }
          if (Array.isArray(feedbackArray)) {
            return feedbackArray.filter((item: any) => item && String(item).trim().length > 0);
          }
          if (typeof feedbackArray === 'string' && feedbackArray.trim().length > 0) {
            return [feedbackArray];
          }
          return [];
        };

        const appreciative = flattenFeedback(response.data.feedback?.appreciative);
        const constructive = flattenFeedback(response.data.feedback?.constructive);

        if (appreciative.length === 0 && constructive.length === 0) {
          setFeedback(null);
          return;
        }

        setFeedback({
          appreciativeFeedback: appreciative.length > 0 ? appreciative : ['No feedback available'],
          constructiveFeedback: constructive.length > 0 ? constructive : ['No feedback available'],
        });
      } else {
        console.warn('⚠️ Feedback fetch failed:', response.error);
        setFeedback(null);
      }
    } catch (error) {
      console.error('❌ Error fetching nomination review feedbacks:', error);
      setFeedback(null);
    } finally {
      setFeedbackLoading(false);
    }
  }, [user, getRelationshipId, nominationReviews]);

  // Fetch nomination reviews when component loads (since review tab is default)
  // Always fetch nomination reviews on initial load if possible
  useEffect(() => {
    if (!authLoading && user && nominationRelationshipId) {
      const relationshipId = getRelationshipId();
      if (!relationshipId) {
        console.error('Missing nomination_relationship_id in URL or navigation state');
        setMissingRelationshipId(true);
        return;
      }
      setMissingRelationshipId(false);
     
      // Don't fetch nomination reviews on initial load - only fetch when user clicks the review tab
      // fetchNominationReviews();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, authLoading, nominationRelationshipId]);

  // Get employee ID from navigation state or nomination reviews
  const getEmployeeId = useCallback(() => {
    const state = location.state as { employeeId?: string } | null;
    return state?.employeeId || nominationReviews?.reviewee?.employee_id;
  }, [location.state, nominationReviews]);

  // Fetch OKR data when OKRs tab is active and employee_id is available
  useEffect(() => {
    const employeeId = getEmployeeId();
    
    if (activeTab === 'okrs' && employeeId && user?.email && !okrLoading && !okrData) {
      // Set employee_id in nominationReviews if not already set
      if (!nominationReviews?.reviewee?.employee_id) {
        const state = location.state as { employeeId?: string; employeeEmail?: string; employeeName?: string } | null;
        setNominationReviews({
          reviewee: {
            employee_id: state?.employeeId || '',
            email: state?.employeeEmail || '',
            name: state?.employeeName || ''
          }
        });
      }
      fetchHODOKRData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, user?.email, location.state]);

  // Handle tab change with API call for feedback and OKRs
  const handleTabChange = (tab: TabType) => {
    setActiveTab(tab);

    // Fetch nomination review details when Review tab is clicked
    if (tab === 'review' && !nominationReviews) {
      fetchNominationReviews();
    }

    // Fetch HOD OKR data when OKRs tab is clicked
    if (tab === 'okrs' && nominationReviews?.reviewee?.employee_id) {
      fetchHODOKRData();
    }

    // Fetch feedback when feedback tab is clicked
    if (tab === 'feedback' && !feedback && !feedbackLoading) {
      fetchFeedback();
    }

    // HOD Review tab - no specific fetch needed for now
  };


  // Handle rating change
  const handleRatingChange = (questionId: string, value: string) => {
    setRatings(prev => ({
      ...prev,
      [questionId]: value
    }));
  };

 

  const handleNotYouSuccess = (newEmail: string) => {
    console.log('Email updated successfully:', newEmail);
    // You can add any additional logic here if needed
  };

  // Handle appreciative feedback change
  const handleAppreciativeFeedbackChange = (value: string) => {
    setAppreciativeFeedback(value);
  };

  // Handle constructive feedback change
  const handleConstructiveFeedbackChange = (value: string) => {
    setConstructiveFeedback(value);
  };

  const closeNotYouDialog = () => {
    setNotYouDialogState({
      isOpen: false,
      email: '',
      emailError: '',
      isSubmitting: false
    });
  };

  const handleNotYouEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setNotYouDialogState(prev => ({
      ...prev,
      email: value,
      emailError: value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value) ? 'Please enter a valid email address' : ''
    }));
  };

  const handleNotYouSubmit = async () => {
    const email = notYouDialogState.email.trim();
    
    if (!email) {
      setNotYouDialogState(prev => ({ ...prev, emailError: 'Email is required' }));
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setNotYouDialogState(prev => ({ ...prev, emailError: 'Please enter a valid email address' }));
      return;
    }

    setNotYouDialogState(prev => ({ ...prev, isSubmitting: true, emailError: '' }));

    try {
      // TODO: Replace with actual API call to update user email
      await new Promise(resolve => setTimeout(resolve, 1000)); // Simulate API call
      
      closeNotYouDialog();
      setShowSuccessDialog(true);
    } catch {
      setNotYouDialogState(prev => ({ 
        ...prev, 
        isSubmitting: false,
        emailError: 'Failed to update email. Please try again.' 
      }));
    }
  };  // Handle Save as Draft
  const handleSaveAsDraft = () => {
    setShowSaveDialog(true);
  };

  // Handle Submit
  const handleSubmit = () => {
    setShowSubmitDialog(true);
  };

  // Confirm Save as Draft
  const handleConfirmSave = async () => {
    setSubmitting(true);
    setShowSaveDialog(false);
    
    try {
      // Temporary: Add real token if not present (for testing purposes)
      if (!localStorage.getItem('authToken')) {
        console.log('🔧 No auth token found, adding real token for testing');
        localStorage.setItem('authToken', '2b043a896a9b05124ada475a9b512a745cb8aab3');
      }
      
      // Get nomination_relationship_id dynamically
      const relationshipId = getRelationshipId();
      if (!relationshipId) {
        console.error('Missing nomination_relationship_id for save operation');
        setErrorMessage('Missing nomination relationship ID. Please refresh the page and try again.');
        setShowErrorDialog(true);
        return;
      }
      
      // Prepare the API payload
      const payload = {
        nomination_relationship_id: parseInt(relationshipId),
        email: user?.email || '',
        competency: {
          solution_focused: parseInt(ratings['rating-1']) || 0,
          adapt_to_win: parseInt(ratings['rating-2']) || 0,
          courage_to_act: parseInt(ratings['rating-3']) || 0,
          act_for_enterprise: parseInt(ratings['rating-4']) || 0,
          own_it: parseInt(ratings['rating-5']) || 0
        },
        feedback: {
          constructive: constructiveFeedback,
          appreciative: appreciativeFeedback
        },
        is_email_required: sendEmailCopy ? "true" : "false"
      };
      
      console.log('💾 Saving nomination review feedback as draft:', payload);
      
      const response = await teamReviewService.saveNominationReviewFeedback(payload);
      
      if (response.status) {
        console.log('✅ Nomination review feedback saved successfully:', response.data);
        // Draft saved successfully
        // Uncheck the email checkbox
        setSendEmailCopy(false);
        // Refetch the nomination review details to update the form with latest data
        await fetchNominationReviews();
        // Show success message
        setShowSuccessDialog(true);
      } else {
        console.error('❌ Failed to save nomination review feedback:', response.error);
        setErrorMessage(response.message || (typeof response.error === 'string' ? response.error : null) || 'Failed to save review');
        setShowErrorDialog(true);
      }
    } catch (error) {
      console.error('❌ Error saving nomination review feedback:', error);
      setErrorMessage(getNominationErrorMessage(error) || 'An error occurred while saving the review.');
      setShowErrorDialog(true);
    } finally {
      setSubmitting(false);
    }
  };

  // Confirm Submit
  const handleConfirmSubmit = async () => {
    setSubmitting(true);
    setShowSubmitDialog(false);
    
    try {
      // Temporary: Add real token if not present (for testing purposes)
      if (!localStorage.getItem('authToken')) {
        console.log('🔧 No auth token found, adding real token for testing');
        localStorage.setItem('authToken', '2b043a896a9b05124ada475a9b512a745cb8aab3');
      }
      
      // Get nomination_relationship_id dynamically
      const relationshipId = getRelationshipId();
      if (!relationshipId) {
        console.error('Missing nomination_relationship_id for submit operation');
        setErrorMessage('Missing nomination relationship ID. Please refresh the page and try again.');
        setShowErrorDialog(true);
        return;
      }
      
      // Prepare the API payload
      const payload = {
        nomination_relationship_id: parseInt(relationshipId),
        email: user?.email || '',
        competency: {
          solution_focused: parseInt(ratings['rating-1']) || 0,
          adapt_to_win: parseInt(ratings['rating-2']) || 0,
          courage_to_act: parseInt(ratings['rating-3']) || 0,
          act_for_enterprise: parseInt(ratings['rating-4']) || 0,
          own_it: parseInt(ratings['rating-5']) || 0
        },
        feedback: {
          constructive: constructiveFeedback,
          appreciative: appreciativeFeedback
        },
        is_email_required: sendEmailCopy ? "true" : "false"
      };
      
      console.log('📤 Submitting nomination review feedback:', payload);
      
      const response = await teamReviewService.submitNominationReviewFeedback(payload);
      
      if (response.status) {
        console.log('✅ Nomination review feedback submitted successfully:', response.data);
        // Uncheck the email checkbox
        setSendEmailCopy(false);
        // Refetch the nomination review details to update the form with latest data
        await fetchNominationReviews();
        // Show success message
        setShowSuccessDialog(true);
      } else {
        console.error('❌ Failed to submit nomination review feedback:', response.error);
        setErrorMessage(response.message || (typeof response.error === 'string' ? response.error : null) || 'Failed to submit review');
        setShowErrorDialog(true);
      }
    } catch (error) {
      console.error('❌ Error submitting nomination review feedback:', error);
      setErrorMessage(getNominationErrorMessage(error) || 'An error occurred while submitting the review.');
      setShowErrorDialog(true);
    } finally {
      setSubmitting(false);
    }
  };

  // Handle OKR rating submission
  const handleSubmitOKRRating = () => {
    setShowOKRSubmitDialog(true);
  };

  // Confirm OKR rating submission
  const handleConfirmOKRSubmit = async () => {
    if (!user?.email || !nominationReviews?.reviewee?.employee_id || okrOverallRating === '' || typeof okrOverallRating !== 'number') {
      setErrorMessage('Missing required data for OKR rating submission');
      setShowErrorDialog(true);
      return;
    }

    setOkrSubmitting(true);
    setShowOKRSubmitDialog(false);
    
    try {
      console.log('📤 Submitting HOD OKR rating:', okrOverallRating);
      
      const response = await hodReviewService.submitHodOKRRating(
        user.email,
        nominationReviews.reviewee.employee_id,
        okrOverallRating
      );
      
      if (response.status) {
        console.log('✅ HOD OKR rating submitted successfully:', response.data);
        // Show success message
        setShowSuccessDialog(true);
        // Optionally refetch OKR data to get updated state
        await fetchHODOKRData();
      } else {
        console.error('❌ Failed to submit HOD OKR rating:', response.error);
        setErrorMessage(response.message || (typeof response.error === 'string' ? response.error : null) || 'Failed to submit OKR rating');
        setShowErrorDialog(true);
      }
    } catch (error) {
      console.error('❌ Error submitting HOD OKR rating:', error);
      setErrorMessage(getNominationErrorMessage(error) || 'An error occurred while submitting the OKR rating.');
      setShowErrorDialog(true);
    } finally {
      setOkrSubmitting(false);
    }
  };

  if (!hasActiveCycle) {
    return <NoActiveCycle />;
  }

  // Show loading state while fetching data
  if (loading) {
    return (
      <div className="min-h-screen flex justify-center items-center">
        <div className="text-center">
          <LoadingSpinner />
          <p className="mt-2 text-sm text-gray-600">Loading team review details...</p>
        </div>
      </div>
    );
  }

  // Only show error states if loading is complete
  if (!loading && !user) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-center">
          <p className="text-red-600">No user data available. Please log in.</p>
        </div>
      </div>
    );
  }

  if (!loading && !reviewDetail) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-center">
          <p className="text-red-600">Team review not found.</p>
          <button
            onClick={handleBackClick}
            className="mt-4 px-4 py-2 bg-primary-800 text-white rounded-lg hover:bg-purple-700 transition-colors"
          >
            Back to Team Reviews
          </button>
        </div>
      </div>
    );
  }

  // Guard clauses to prevent rendering with null data
  if (!user || !reviewDetail) {
    return null;
  }

  return (
    <div className="space-y-6">
      {/* Header with Back Button */}
      <div className="flex items-center gap-4">
        <button
          onClick={handleBackClick}
          className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
        >
          <img src={arrowBackIcon} alt="Back" className="w-5 h-5" />
          <span className='text-primary-800'>Back to Team List</span>
        </button>
      </div>

      {/* Tabs Navigation */}
      {/* Tabs Navigation */}
      <div className="responsive-tabs flex gap-2">
        <button
          onClick={() => handleTabChange('okrs')}
          className={`px-6 py-3 rounded-lg font-medium flex items-center gap-3 transition-colors ${
            activeTab === 'okrs'
              ? 'bg-purple-100 text-purple-800 shadow-md outline outline-1 outline-purple-800'
              : 'text-primary-400 hover:bg-gray-200'
          }`}
          role="tab"
          aria-selected={activeTab === 'okrs'}
        >
          <img src={tabViewOKRsIcon} alt="" className="w-5 h-5" />
          <span className={activeTab === 'okrs' ? 'text-base' : 'text-base'}>
            Self Ratings & OKRs
          </span>
        </button>
        {/* Manager 360 Feedback tab - commented out
        <button
          onClick={() => handleTabChange('review')}
          className={`px-6 py-3 rounded-lg font-medium flex items-center gap-3 transition-colors ${
            activeTab === 'review'
              ? 'bg-purple-100 text-purple-800 shadow-md outline outline-1 outline-purple-800'
              : 'text-primary-400 hover:bg-gray-200'
          }`}
          role="tab"
          aria-selected={activeTab === 'review'}
        >
          <img src={tabReviewIcon} alt="" className="w-5 h-5" />
          <span className={activeTab === 'review' ? 'text-base' : 'text-base'}>
            Manager 360 Feedback
          </span>
        </button>
        */}
        <button
          onClick={() => handleTabChange('feedback')}
          className={`px-6 py-3 rounded-lg font-medium flex items-center gap-3 transition-colors ${
            activeTab === 'feedback'
              ? 'bg-purple-100 text-purple-800 shadow-md outline outline-1 outline-purple-800'
              : 'text-primary-400 hover:bg-gray-200'
          }`}
          role="tab"
          aria-selected={activeTab === 'feedback'}
        >
          <img src={tabViewFeedbackIcon} alt="" className="w-6 h-6" />
          <span className={activeTab === 'feedback' ? 'text-base' : 'text-base'}>
            Team Review and Feedback
          </span>
        </button>
      </div>








      {/* Tab Content */}
      {activeTab === 'hodReview' && (
        <div className="space-y-6">
          {missingRelationshipId ? (
            <Card>
              <div className="p-6 text-center">
                <div className="text-red-500">
                  <svg className="mx-auto h-12 w-12 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                  <h3 className="mt-2 text-sm font-medium text-red-900">Missing Nomination Relationship ID</h3>
                  <p className="mt-1 text-sm text-red-700">
                    The nomination relationship ID is required but was not found in the URL or navigation state.
                  </p>
                  <p className="mt-1 text-sm text-red-600">
                    Please navigate to this page from the team reviews list with the correct parameters.
                  </p>
                  <button
                    onClick={handleBackClick}
                    className="mt-3 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                  >
                    Back to Team Reviews
                  </button>
                </div>
              </div>
            </Card>
          ) : nominationReviewsLoading ? (
            <Card>
              <div className="p-6 text-center">
                <LoadingSpinner />
                <p className="mt-2 text-sm text-gray-600">Loading review details...</p>
              </div>
            </Card>
          ) : (
            <>
              {/* Employee Information using reusable component */}
              <Card>
                <div className="">
                  {!nominationReviews ? (
                    <div className="p-6 text-center">
                      <LoadingSpinner />
                      <p className="mt-2 text-sm text-gray-600">Loading employee information...</p>
                    </div>
                  ) : (
                    <EmployeeInformation
                        employee={{
                          employeeId: (() => {
                            const state = location.state as { employeeId?: string } | null;
                            const empId = state?.employeeId || nominationReviews?.reviewee?.employee_id || reviewDetail.employeeId;
                            return empId?.startsWith('#') ? empId : `#${empId}`;
                          })(),
                          employeeName: (() => {
                            const state = location.state as { employeeName?: string } | null;
                            return state?.employeeName || nominationReviews?.reviewee?.name || reviewDetail.employeeName;
                          })(),
                          designation: (() => {
                            const state = location.state as { designation?: string } | null;
                            return state?.designation || nominationReviews?.reviewee?.designation || reviewDetail.designation;
                          })(),
                          department: (() => {
                            const state = location.state as { department?: string } | null;
                            return state?.department || nominationReviews?.reviewee?.department || reviewDetail.department;
                          })(),
                          email: (() => {
                            const state = location.state as { employeeEmail?: string } | null;
                            return state?.employeeEmail || nominationReviews?.reviewee?.email || reviewDetail.employeeEmail;
                          })(),
                          managerName: (() => {
                            const state = location.state as { managerName?: string } | null;
                            return state?.managerName || nominationReviews?.reviewee?.manager?.manager_name || reviewDetail.managerName;
                          })(),
                          managerEmail: (() => {
                            const state = location.state as { managerEmail?: string } | null;
                            return state?.managerEmail || nominationReviews?.reviewee?.manager?.manager_email;
                          })()
                        }}
                      title="Employee Information"
                    />
                  )}
                </div>
              </Card>

          {/* Rating Table */}
          <Card>
            <div className="overflow-x-auto">
              <div className="bg-purple-800 text-white p-3 rounded-t-lg">
                <div className="responsive-wide-grid grid gap-4 font-medium" style={{gridTemplateColumns: '80px 200px 1fr 1fr 1fr 1fr'}}>
                  <div className="w-24">S.NO.</div>
                  <div>Description/Rating</div>
                  <div className='text-center'>1-Excel</div>
                  <div className='text-center'>2-Fully Meet</div>
                  <div className='text-center'>3-Mostly Meet</div>
                  <div className='text-center'>4-Require Action</div>
                </div>
              </div>
              
              <div className="border border-gray-200 rounded-b-lg">
                {[
                  { sno: 1, description: 'Solution Focused', tooltip: 'I prioritise customer needs through value-added solutions.' },
                  { sno: 2, description: 'Adapt to Win', tooltip: 'I learn, unlearn and re-learn to succeed.' },
                  { sno: 3, description: 'Courage to Act', tooltip: 'I speak up, challenge norms, and act with purpose and pace.' },
                  { sno: 4, description: 'Act for Enterprise', tooltip: 'I act on what\'s best for PETRONAS.' },
                  { sno: 5, description: 'Own It', tooltip: 'I am accountable to deliver outcomes that matter.' }
                ].map((item, index) => (
                  <div key={item.sno} className={`responsive-wide-grid grid gap-4 p-3 ${index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}`} style={{gridTemplateColumns: '80px 200px 1fr 1fr 1fr 1fr'}}>
                    <div className="font-medium pl-2">{item.sno}.</div>
                    <div className="text-left">
                      <div className="flex items-center space-x-2">
                        <span>{item.description}</span>
                        {item.tooltip && (
                          <div className="relative group">
                            <svg 
                              className="w-4 h-4 text-gray-400 hover:text-gray-600 cursor-help" 
                              fill="none" 
                              stroke="currentColor" 
                              viewBox="0 0 24 24"
                            >
                              <path 
                                strokeLinecap="round" 
                                strokeLinejoin="round" 
                                strokeWidth={2} 
                                d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" 
                              />
                            </svg>
                            <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2 text-white text-xs rounded-lg px-3 py-2 hidden group-hover:block pointer-events-none z-50 w-64 text-center border border-gray-600 shadow-lg" style={{backgroundColor: '#1f2937'}}>
                              {item.tooltip}
                              <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent" style={{borderTopColor: '#1f2937'}}></div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className='text-center'>
                      <input
                        type="radio"
                        name={`rating-${item.sno}`}
                        className="w-4 h-4 text-purple-600"
                        value="1"
                        checked={ratings[`rating-${item.sno}`] === '1'}
                        onChange={(e) => handleRatingChange(`rating-${item.sno}`, e.target.value)}
                        disabled={isFormDisabled}
                      />
                    </div>
                    <div className='text-center'>
                      <input
                        type="radio"
                        name={`rating-${item.sno}`}
                        className="w-4 h-4 text-purple-600"
                        value="2"
                        checked={ratings[`rating-${item.sno}`] === '2'}
                        onChange={(e) => handleRatingChange(`rating-${item.sno}`, e.target.value)}
                        disabled={isFormDisabled}
                      />
                    </div>
                    <div className='text-center'>
                      <input
                        type="radio"
                        name={`rating-${item.sno}`}
                        className="w-4 h-4 text-purple-600"
                        value="3"
                        checked={ratings[`rating-${item.sno}`] === '3'}
                        onChange={(e) => handleRatingChange(`rating-${item.sno}`, e.target.value)}
                        disabled={isFormDisabled}
                      />
                    </div>
                    <div className='text-center'>
                      <input
                        type="radio"
                        name={`rating-${item.sno}`}
                        className="w-4 h-4 text-purple-600"
                        value="4"
                        checked={ratings[`rating-${item.sno}`] === '4'}
                        onChange={(e) => handleRatingChange(`rating-${item.sno}`, e.target.value)}
                        disabled={isFormDisabled}
                      />
                    </div>
                  </div>
                ))}
              </div>

             

              {/* Feedback Section */}
              <div className="mt-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Add your Appreciative Feedback for the Employee
                </label>
                <textarea
                  rows={4}
                  placeholder="(10-10000 characters)"
                  className={`w-full border border-gray-300 rounded px-3 py-2 ${
                    isFormDisabled ? 'bg-gray-100 cursor-not-allowed' : ''
                  }`}
                  value={appreciativeFeedback}
                  onChange={(e) => handleAppreciativeFeedbackChange(e.target.value)}
                  disabled={isFormDisabled}
                />
                <div className="text-sm text-gray-500 mt-1">
                  {appreciativeFeedback.length} characters
                  {appreciativeFeedback.length < 10 && (
                    <span className="text-red-500 ml-2">Minimum 10 characters required</span>
                  )}
                </div>
              </div>

              {/* Constructive Feedback Section */}
              <div className="mt-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Add your Constructive Feedback for the Employee
                </label>
                <textarea
                  rows={4}
                  placeholder="(10-10000 characters)"
                  className={`w-full border border-gray-300 rounded px-3 py-2 ${
                    isFormDisabled ? 'bg-gray-100 cursor-not-allowed' : ''
                  }`}
                  value={constructiveFeedback}
                  onChange={(e) => handleConstructiveFeedbackChange(e.target.value)}
                  disabled={isFormDisabled}
                />
                <div className="text-sm text-gray-500 mt-1">
                  {constructiveFeedback.length} characters
                  {constructiveFeedback.length < 10 && (
                    <span className="text-red-500 ml-2">Minimum 10 characters required</span>
                  )}
                </div>
              </div>    





              {/* Submit Section */}
              {!isFormDisabled && (
                <div className="mt-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                  <div className="flex items-center gap-2">
                   
                
                  </div>
                  <div className="flex gap-3">

 <button 
                      className={`px-4 py-2 text-sm sm:text-base inline-flex items-center justify-center font-medium rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 touch-target min-h-[44px] min-w-[44px] border ${
                        isDraftSaveable() && !submitting
                          ? 'text-purple-600 border-purple-600 hover:bg-purple-50 bg-white focus:ring-purple-500'
                          : 'border-gray-400 bg-white text-gray-600 cursor-not-allowed opacity-50'
                      }`}
                      onClick={handleSaveAsDraft}
                      disabled={submitting || !isDraftSaveable()}
                    >
                      {submitting ? 'Saving...' : 'Save as Draft'}
                    </button>


                    
                    <button 
                      className={`px-4 py-2 text-sm sm:text-base inline-flex items-center justify-center font-medium rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 touch-target min-h-[44px] min-w-[44px] ${
                        isFormComplete() && !submitting
                          ? 'bg-purple-800 hover:bg-purple-700 text-white focus:ring-purple-500'
                          : 'bg-purple-800 text-white opacity-50 cursor-not-allowed'
                      }`}
                      onClick={handleSubmit}
                      disabled={submitting || !isFormComplete()}
                    >
                      {submitting ? 'Submitting...' : 'Submit'}
                    </button>

                  </div>
                </div>
              )}
                </div>
              </Card>

                 {/* Status Information */}
         {((nominationReviews?.status) || reviewDetail?.reviewStatus) && (
         ((nominationReviews?.status || reviewDetail?.reviewStatus)?.toLowerCase() === 'submitted' ||
         (nominationReviews?.status || reviewDetail?.reviewStatus)?.toLowerCase() === 'approved' ||
         ((nominationReviews?.status || reviewDetail?.reviewStatus) && (nominationReviews?.status || reviewDetail?.reviewStatus)?.toLowerCase() !== 'pending')) && (
          <Card>
            <div className="">
              <div className={`rounded-lg p-4 ${
                (nominationReviews?.status || reviewDetail?.reviewStatus)?.toLowerCase() === 'submitted' 
                  ? 'bg-green-50 border border-green-200' 
                  : (nominationReviews?.status || reviewDetail?.reviewStatus)?.toLowerCase() === 'approved'
                  ? 'bg-blue-50 border border-blue-200'
                  : 'bg-gray-50 border border-gray-200'
              }`}>
                <div className="flex">
                  <div className="flex-shrink-0">
                    {(nominationReviews?.status || reviewDetail?.reviewStatus)?.toLowerCase() === 'submitted' ? (
                      <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                    ) : (nominationReviews?.status || reviewDetail?.reviewStatus)?.toLowerCase() === 'approved' ? (
                      <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                    ) : (
                      <svg className="h-5 w-5 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                    )}
                  </div>
                  <div className="ml-3">
                    <h3 className={`text-sm font-medium ${
                      (nominationReviews?.status || reviewDetail?.reviewStatus)?.toLowerCase() === 'submitted' 
                        ? 'text-green-800' 
                        : (nominationReviews?.status || reviewDetail?.reviewStatus)?.toLowerCase() === 'approved'
                        ? 'text-blue-800'
                        : 'text-gray-800'
                    }`}>
                      Review {((nominationReviews?.status || reviewDetail?.reviewStatus)?.charAt(0).toUpperCase() || '') + ((nominationReviews?.status || reviewDetail?.reviewStatus)?.slice(1) || '')}
                    </h3>
                    <div className={`mt-2 text-sm ${
                      (nominationReviews?.status || reviewDetail?.reviewStatus)?.toLowerCase() === 'submitted' 
                        ? 'text-green-700' 
                        : (nominationReviews?.status || reviewDetail?.reviewStatus)?.toLowerCase() === 'approved'
                        ? 'text-blue-700'
                        : 'text-gray-700'
                    }`}>
                      <p>
                        {(nominationReviews?.status || reviewDetail?.reviewStatus)?.toLowerCase() === 'submitted' 
                          ? 'This team review has been successfully submitted.' 
                          : (nominationReviews?.status || reviewDetail?.reviewStatus)?.toLowerCase() === 'approved'
                          ? 'This team review has been approved.'
                          : `This team review status is: ${(nominationReviews?.status || reviewDetail?.reviewStatus)}.`
                        }
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Card>
        )
      )}
            </>
          )}
        </div>
      )}      










      

      {/* Manager 360 Feedback tab content - commented out
      {activeTab === 'review' && (
        <div className="space-y-6">
          {missingRelationshipId ? (
            <Card>
              <div className="p-6 text-center">
                <div className="text-red-500">
                  <svg className="mx-auto h-12 w-12 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                  <h3 className="mt-2 text-sm font-medium text-red-900">Missing Nomination Relationship ID</h3>
                  <p className="mt-1 text-sm text-red-700">
                    The nomination relationship ID is required but was not found in the URL or navigation state.
                  </p>
                  <p className="mt-1 text-sm text-red-600">
                    Please navigate to this page from the team reviews list with the correct parameters.
                  </p>
                  <button
                    onClick={handleBackClick}
                    className="mt-3 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                  >
                    Back to Team Reviews
                  </button>
                </div>
              </div>
            </Card>
          ) : nominationReviewsLoading ? (
            <Card>
              <div className="p-6 text-center">
                <LoadingSpinner />
                <p className="mt-2 text-sm text-gray-600">Loading review details...</p>
              </div>
            </Card>
          ) : (
            <>
              <Card>
                <div className="">
                  {!nominationReviews ? (
                    <div className="p-6 text-center">
                      <LoadingSpinner />
                      <p className="mt-2 text-sm text-gray-600">Loading employee information...</p>
                    </div>
                  ) : (
                    <EmployeeInformation
                      employee={{
                        employeeId: (() => {
                          const state = location.state as { employeeId?: string } | null;
                          const empId = state?.employeeId || nominationReviews?.reviewee?.employee_id || reviewDetail.employeeId;
                          return empId?.startsWith('#') ? empId : `#${empId}`;
                        })(),
                        employeeName: (() => {
                          const state = location.state as { employeeName?: string } | null;
                          return state?.employeeName || nominationReviews?.reviewee?.name || reviewDetail.employeeName;
                        })(),
                        designation: (() => {
                          const state = location.state as { designation?: string } | null;
                          return state?.designation || nominationReviews?.reviewee?.designation || reviewDetail.designation;
                        })(),
                        department: (() => {
                          const state = location.state as { department?: string } | null;
                          return state?.department || nominationReviews?.reviewee?.department || reviewDetail.department;
                        })(),
                        email: (() => {
                          const state = location.state as { employeeEmail?: string } | null;
                          return state?.employeeEmail || nominationReviews?.reviewee?.email || reviewDetail.employeeEmail;
                        })(),
                        managerName: (() => {
                          const state = location.state as { managerName?: string } | null;
                          return state?.managerName || nominationReviews?.reviewee?.manager?.manager_name || reviewDetail.managerName;
                        })(),
                        managerEmail: (() => {
                          const state = location.state as { managerEmail?: string } | null;
                          return state?.managerEmail || nominationReviews?.reviewee?.manager?.manager_email;
                        })()
                      }}
                      title="Employee Information"
                    />
                  )}
                </div>
              </Card>

              <ManagerReview
                employeeId={nominationReviews?.reviewee?.employee_id || reviewDetail.employeeId}
              />
            </>
          )}
        </div>
      )}
      */}

      {activeTab === 'feedback' && (
        <div className="space-y-6">
          {/* Employee Information Card */}
          <Card>
            <div className="">
              {!nominationReviews ? (
                <div className="p-6 text-center">
                  <LoadingSpinner />
                  <p className="mt-2 text-sm text-gray-600">Loading employee information...</p>
                </div>
              ) : (
                <EmployeeInformation
                  employee={{
                    employeeId: (() => {
                      const state = location.state as { employeeId?: string } | null;
                      const empId = state?.employeeId || nominationReviews?.reviewee?.employee_id || reviewDetail.employeeId;
                      return empId?.startsWith('#') ? empId : `#${empId}`;
                    })(),
                    employeeName: (() => {
                      const state = location.state as { employeeName?: string } | null;
                      return state?.employeeName || nominationReviews?.reviewee?.name || reviewDetail.employeeName;
                    })(),
                    designation: (() => {
                      const state = location.state as { designation?: string } | null;
                      return state?.designation || nominationReviews?.reviewee?.designation || reviewDetail.designation;
                    })(),
                    department: (() => {
                      const state = location.state as { department?: string } | null;
                      return state?.department || nominationReviews?.reviewee?.department || reviewDetail.department;
                    })(),
                    email: (() => {
                      const state = location.state as { employeeEmail?: string } | null;
                      return state?.employeeEmail || nominationReviews?.reviewee?.email || reviewDetail.employeeEmail;
                    })(),
                    managerName: (() => {
                      const state = location.state as { managerName?: string } | null;
                      return state?.managerName || nominationReviews?.reviewee?.manager?.manager_name || reviewDetail.managerName;
                    })(),
                    managerEmail: (() => {
                      const state = location.state as { managerEmail?: string } | null;
                      return state?.managerEmail || nominationReviews?.reviewee?.manager?.manager_email;
                    })()
                  }}
                  title="Employee Information"
                />
              )}
            </div>
          </Card>

          {feedbackLoading ? (
            <Card>
              <div className="p-6 text-center">
                <LoadingSpinner />
                <p className="mt-2 text-sm text-gray-600">Loading feedback...</p>
              </div>
            </Card>
          ) : feedback ? (
            <>
              {/* Appreciative Feedback */}
              <Card>
                <div className="p-6 border rounded-lg border-gray-200">
                  <h3 className="text-sm font-semibold text-gray-900 mb-4">
                    Appreciative Feedback <span className="font-normal">for the Employee</span>
                  </h3>
                  <div className="space-y-4">
                    {feedback.appreciativeFeedback.map((item, index) => (
                      <div key={index} className="border border-gray-400 rounded-lg p-4">
                        <p className="text-gray-700 leading-relaxed">{item}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </Card>

              {/* Constructive Feedback */}
              <Card>
                <div className="p-6 border rounded-lg border-gray-200">
                  <h3 className="text-sm font-semibold text-gray-900 mb-4">
                    Constructive Feedback <span className="font-normal">for the Employee</span>
                  </h3>
                  <div className="space-y-4">
                    {feedback.constructiveFeedback.map((item, index) => (
                      <div key={index} className="border border-gray-400 rounded-lg p-4">
                        <p className="text-gray-700 leading-relaxed">{item}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </Card>
            </>
          ) : (
            <Card>
              <div className="p-6">
                <NoDataState
                  title="No Feedback Submitted"
                  description="The team review feedback has not been submitted for this employee yet."
                />
              </div>
            </Card>
          )}
        </div>
      )}

      {activeTab === 'okrs' && (
        <div className="space-y-6">
          {/* Employee Information Card */}
          <Card>
            <div className="">
              {!nominationReviews ? (
                <div className="p-6 text-center">
                  <LoadingSpinner />
                  <p className="mt-2 text-sm text-gray-600">Loading employee information...</p>
                </div>
              ) : (
                <EmployeeInformation
                  employee={{
                    employeeId: nominationReviews?.reviewee?.employee_id ? `#${nominationReviews.reviewee.employee_id}` : reviewDetail.employeeId,
                    employeeName: nominationReviews?.reviewee?.name || reviewDetail.employeeName,
                    designation: nominationReviews?.reviewee?.designation || reviewDetail.designation,
                    department: nominationReviews?.reviewee?.department || reviewDetail.department,
                    email: nominationReviews?.reviewee?.email || reviewDetail.employeeEmail,
                    managerName: nominationReviews?.reviewee?.manager?.manager_name || reviewDetail.managerName
                  }}
                  title="Employee Information"
                />
              )}
            </div>
          </Card>

          <Card>
            <div className="p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-6">Self Ratings & OKRs</h2>
              
              {/* OKR Table */}
              <div className="mb-6">
                {okrLoading ? (
                  <div className="bg-gray-50 rounded-lg p-8 text-center">
                    <LoadingSpinner />
                    <p className="mt-2 text-sm text-gray-600">Loading OKR data...</p>
                  </div>
                ) : okrError ? (
                  <div className="bg-red-50 rounded-lg p-4 text-center">
                    <p className="text-red-600 text-sm">{okrError}</p>
                  </div>
                ) : okrData?.self_appraisal_list?.[0]?.okrs ? (
                  <div
                    className="max-w-full overflow-x-auto rounded-lg border border-gray-200"
                    role="region"
                    aria-label="Employee OKRs table"
                    tabIndex={0}
                  >
                    <div className="min-w-[760px]">
                    <div className="bg-purple-800 text-white p-3">
                      <div className="grid grid-cols-5 gap-4 font-medium">
                        <div className="text-center">S.NO.</div>
                        <div>Objective</div>
                        <div>Target</div>
                        <div>{resultColumnLabel}</div>
                        <div className="text-center">Rating</div>
                      </div>
                    </div>
                    <div>
                      {okrData.self_appraisal_list[0].okrs.map((okr: any, index: number) => (
                        <div key={index} className={`grid grid-cols-5 gap-4 p-3 ${index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}`}>
                          <div className="text-center font-medium">{index + 1}</div>
                          <div>{okr.objective}</div>
                          <div>{okr.target}</div>
                          <div>{okr.eyr}</div>
                          <div className="text-center">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                              {okr.rating === 1 ? '1-Excel' : 
                               okr.rating === 2 ? '2-Fully Met' : 
                               okr.rating === 3 ? '3-Mostly Met' : 
                               okr.rating === 4 ? '4-Requires Action' : okr.rating}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                    </div>
                  </div>
                ) : (
                  <div className="bg-gray-50 rounded-lg p-8 text-center">
                    <div className="flex flex-col items-center justify-center space-y-3">
                      {/* Document Icon */}
                      <svg className="w-16 h-16 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                      </svg>
                      <div className="text-center">
                        <h3 className="text-lg font-medium text-gray-900 mb-1">No OKR Data Found</h3>
                        <p className="text-gray-500 text-sm">This employee has not submitted any OKR data yet.</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Overall Rating Section - Only show when OKR data exists */}
              {okrData?.self_appraisal_list?.[0]?.okrs && (
                <div className="bg-gray-50 border-t border-gray-200 p-4 rounded-b-lg">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-4">
                      <label className="text-sm font-medium text-gray-900">
                        Overall Rating
                      </label>
                      <div className="flex items-center space-x-2">
                        <select
                          value={okrOverallRating}
                          onChange={(e) => setOkrOverallRating(e.target.value === '' ? '' : Number(e.target.value))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 bg-white sm:w-48"
                        >
                          <option value="">Select Rating</option>
                          <option value={1}>1-Excel</option>
                          <option value={2}>2-Fully Met</option>
                          <option value={3}>3-Mostly Met</option>
                          <option value={4}>4-Requires Action</option>
                        </select>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Submit Section - Only show when OKR data exists */}
              {okrData?.self_appraisal_list?.[0]?.okrs && (
                <div className="mt-4 p-6 bg-white border rounded-lg">
                  <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                    <div className="flex-1">
                      <h3 className="text-lg font-medium text-gray-900">Submit Overall Rating</h3>
                      <p className="mt-1 text-sm text-gray-600">
                      Manager has submitted an overall rating for this employee. You can overwrite the rating or keep it the same while submitting.
                      </p>
                    </div>
                    <div className="flex w-full gap-3 sm:w-auto">
                      <button 
                        className={`px-6 py-2 text-sm font-medium rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                          okrOverallRating !== '' && okrOverallRating > 0 && okrOverallRating <= 4 && !okrSubmitting
                            ? 'bg-purple-800 hover:bg-purple-700 text-white focus:ring-purple-500'
                            : 'bg-purple-800 text-white opacity-50 cursor-not-allowed'
                        }`}
                        onClick={handleSubmitOKRRating}
                        disabled={!(okrOverallRating !== '' && okrOverallRating > 0 && okrOverallRating <= 4) || okrSubmitting}
                      >
                        {okrSubmitting ? 'Submitting...' : 'Submit Overall Rating'}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </Card>
        </div>
      )}

   

      {/* Not You Dialog */}
      <NotYouDialog
        isOpen={showNotYouDialog}
        onClose={() => setShowNotYouDialog(false)}
        currentEmail={user?.email || 'your.email@company.com'}
        onSuccess={handleNotYouSuccess}
      />

      {/* Not You Dialog */}
      <Dialog
        isOpen={notYouDialogState.isOpen}
        onClose={closeNotYouDialog}
        title="Not You?"
        type="info"
        showCancel={true}
        cancelText="Cancel"
        confirmText={notYouDialogState.isSubmitting ? "Updating..." : "Update Email"}
        onConfirm={handleNotYouSubmit}
        message={
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              If this profile doesn't belong to you, please provide your correct email address and we'll update your account.
            </p>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Your Email Address
              </label>
              <input
                type="email"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter your email"
                value={notYouDialogState.email}
                onChange={handleNotYouEmailChange}
                disabled={notYouDialogState.isSubmitting}
              />
              {notYouDialogState.emailError && (
                <p className="mt-1 text-sm text-red-600">{notYouDialogState.emailError}</p>
              )}
            </div>
          </div>
        }
      />

      {/* Save as Draft Dialog */}
      <Dialog
        isOpen={showSaveDialog}
        onClose={() => setShowSaveDialog(false)}
        title="Save as Draft"
        type="info"
        showCancel={true}
        cancelText="Cancel"
        confirmText="Save"
        onConfirm={handleConfirmSave}
        message={
          <div className="space-y-4">
            <p className="text-gray-700">
              Are you sure you want to save this review as a draft? You can continue editing it later.
            </p>
            <p className="text-gray-600 text-sm">
              Your progress will be saved and you can complete the review at any time.
            </p>
          </div>
        }
      />

      {/* Submit Confirmation Dialog */}
      <Dialog
        isOpen={showSubmitDialog}
        onClose={() => setShowSubmitDialog(false)}
        title="Submit Review"
        type="warning"
        showCancel={true}
        cancelText="Cancel"
        confirmText="Submit"
        onConfirm={handleConfirmSubmit}
        message={
          <div className="space-y-4">
            <p className="text-gray-700">
              Are you sure you want to submit this team review for <strong>{nominationReviews?.reviewee?.name || reviewDetail?.employeeName || 'this employee'}</strong>?
            </p>
            <p className="text-gray-600 text-sm">
              Once submitted, you will not be able to make any changes to this review.
            </p>
          </div>
        }
      />

      {/* OKR Submit Confirmation Dialog */}
      <Dialog
        isOpen={showOKRSubmitDialog}
        onClose={() => setShowOKRSubmitDialog(false)}
        title="Submit OKR Rating"
        type="warning"
        showCancel={true}
        cancelText="Cancel"
        confirmText="Submit"
        onConfirm={handleConfirmOKRSubmit}
        message={
          <div className="space-y-4">
            <p className="text-gray-700">
              Are you sure you want to submit the overall OKR rating of <strong>{okrOverallRating === 1 ? '1-Excel' : 
               okrOverallRating === 2 ? '2-Fully Met' : 
               okrOverallRating === 3 ? '3-Mostly Met' : 
               okrOverallRating === 4 ? '4-Requires Action' : okrOverallRating}</strong> for <strong>{nominationReviews?.reviewee?.name || reviewDetail?.employeeName || 'this employee'}</strong>?
            </p>
            <p className="text-gray-600 text-sm">
              Once submitted, this rating will be saved to the system.
            </p>
          </div>
        }
      />

      {/* Success Dialog */}
      <Dialog
        isOpen={showSuccessDialog}
        onClose={() => {
          setShowSuccessDialog(false);
        }}
        title="Success"
        type="success"
        confirmText="OK"
        onConfirm={() => {
          setShowSuccessDialog(false);
        }}
        message={
          <div className="space-y-4">
            <p className="text-gray-700">
              Your HOD review has been successfully saved!
            </p>
            <p className="text-gray-600 text-sm">
              You can continue editing or use the back button to return to the HOD reviews list.
            </p>
          </div>
        }
      />

      {/* Error Dialog */}
      <Dialog
        isOpen={showErrorDialog}
        onClose={() => {
          setShowErrorDialog(false);
          setErrorMessage('');
        }}
        title="Error"
        type="error"
        confirmText="OK"
        onConfirm={() => {
          setShowErrorDialog(false);
          setErrorMessage('');
        }}
        message={
          <div className="space-y-4">
            <p className="text-gray-700">
              {errorMessage}
            </p>
          </div>
        }
      />
    </div>
  );
};

export default HODReviewDetailPageView;
